import React, { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Plus,
  Wand2,
  ExternalLink,
  Film,
  Tv,
  CalendarClock,
  Clock,
  ChevronDown,
  ChevronRight,
  User,
  Bot,
} from "lucide-react";

// Poll the /latest endpoint every 8s so the report auto-refreshes while
// the sweep runs in background (typically 5-10 min for the full pass).
const POLL_MS = 8000;

export const AdminCoverageAudit = () => {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [applyMode, setApplyMode] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [lookbackDays, setLookbackDays] = useState(""); // "" = default (30j côté backend)

  const fetchAll = useCallback(async () => {
    try {
      const [latestRes, historyRes] = await Promise.all([
        api.get("/coverage-audit/latest"),
        api.get("/coverage-audit/history?limit=20"),
      ]);
      setLatest(latestRes.data && latestRes.data.status !== "never_run" ? latestRes.data : null);
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch (e) {
      toast.error("Impossible de charger l'historique des audits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  const runAudit = async (apply) => {
    setStarting(true);
    setApplyMode(apply);
    try {
      // Only forward lookback_days when the admin explicitly entered a value
      // AND is launching a backfill (apply=true). The read-only audit uses
      // the default window to stay fast.
      const payload = { apply };
      const parsed = parseInt(lookbackDays, 10);
      if (apply && Number.isFinite(parsed) && parsed > 0) {
        payload.lookback_days = parsed;
      }
      await api.post("/coverage-audit/run", payload);
      toast.success(
        apply
          ? payload.lookback_days
            ? `Audit + rattrapage ${payload.lookback_days}j lancés. Peut prendre 10-20 min. Le rapport se met à jour tout seul.`
            : "Audit + rattrapage lancés (5-10 min). Le rapport se met à jour tout seul."
          : "Audit lancé (5-10 min). Le rapport se met à jour tout seul."
      );
      // Optimistic placeholder so user sees "running" immediately
      setLatest({
        status: "running",
        started_at: new Date().toISOString(),
        scanned_tmdb: 0,
        missing: [],
        title_stale: [],
        apply,
        lookback_days_override: payload.lookback_days || null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer l'audit");
    } finally {
      setStarting(false);
    }
  };

  const fixOne = async (row, kind) => {
    const key = `${row.tmdb_id}:${kind}`;
    setBusyRow(key);
    try {
      const { data } = await api.post(
        `/coverage-audit/apply-one/${row.tmdb_id}?kind=${encodeURIComponent(row.type)}`
      );
      toast.success(
        data.action === "added"
          ? `« ${data.title} » ajoutée à la base`
          : `Fiche rafraîchie (${(data.changes || []).join(", ") || "aucun changement"})`
      );
      // Remove the row locally for immediate feedback; the next poll will
      // reconcile with server truth.
      setLatest((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          missing: (prev.missing || []).filter((r) => r.tmdb_id !== row.tmdb_id),
          title_stale: (prev.title_stale || []).filter((r) => r.tmdb_id !== row.tmdb_id),
        };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correction échouée");
    } finally {
      setBusyRow(null);
    }
  };

  if (loading) return <div className="text-neutral-500 text-sm">Chargement…</div>;

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" /> Audit de couverture
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">
            Détection proactive des trous du catalogue
          </h1>
          <p className="text-neutral-600 mt-2 max-w-2xl leading-relaxed">
            Cet outil balaye <strong>toutes</strong> les pages TMDB (sans le cap de 1 000 titres du sync quotidien) pour chaque plateforme cible sur la fenêtre courante, puis compare à la base pour signaler les titres <em>manquants</em> et les <em>titres périmés</em> (traduction FR ajoutée après coup).
            <br />
            Un audit automatique tourne aussi tous les <strong>dimanches à 22 h UTC</strong> avec rattrapage activé.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            data-testid="audit-run-btn"
            onClick={() => runAudit(false)}
            disabled={starting || latest?.status === "running"}
            className="btn-ghost"
          >
            {starting && !applyMode ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Lancer un audit
          </button>
          <label
            className="inline-flex items-center gap-1.5 text-xs text-neutral-600"
            title="Fenêtre de rattrapage en jours. Laisser vide pour le défaut (30j). Utile pour récupérer des titres streaming manqués depuis le lancement du site : essayer 180."
          >
            Rattrapage sur
            <input
              type="number"
              min="1"
              max="365"
              step="1"
              placeholder="30"
              value={lookbackDays}
              onChange={(e) => setLookbackDays(e.target.value)}
              disabled={starting || latest?.status === "running"}
              data-testid="audit-lookback-input"
              className="w-16 px-2 py-1 border border-neutral-300 rounded text-sm text-right focus:outline-none focus:border-[var(--red)]"
            />
            jours
          </label>
          <button
            data-testid="audit-run-apply-btn"
            onClick={() => {
              const parsed = parseInt(lookbackDays, 10);
              const customLookback = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
              const warning = customLookback
                ? `Lancer un audit + rattrapage sur ${customLookback} jours ?\n\n` +
                  "Tous les titres manquants détectés sur cette fenêtre élargie seront ajoutés " +
                  "à la base (et à Notion). Cette opération peut prendre 10 à 20 minutes."
                : "Lancer un audit + rattrapage automatique ?\n\n" +
                  "Tous les titres manquants détectés seront ajoutés à la base " +
                  "(et à Notion), et tous les titres périmés seront rafraîchis. " +
                  "Cette opération peut prendre 5 à 10 minutes.";
              if (!window.confirm(warning)) return;
              runAudit(true);
            }}
            disabled={starting || latest?.status === "running"}
            className="btn-red"
          >
            {starting && applyMode ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Audit + rattrapage auto
          </button>
        </div>
      </header>

      {!latest && (
        <div className="bg-white border border-[var(--line)] rounded-md p-8 text-center text-neutral-500">
          Aucun audit lancé pour l'instant. Cliquez sur <em>Lancer un audit</em> pour démarrer le premier scan.
        </div>
      )}

      {latest && (
        <>
          <Summary latest={latest} />
          <div className="grid md:grid-cols-2 gap-6">
            <MissingList rows={latest.missing || []} onFix={fixOne} busyRow={busyRow} />
            <StaleTitlesList rows={latest.title_stale || []} onFix={fixOne} busyRow={busyRow} />
          </div>
          {(latest.errors || []).length > 0 && (
            <ErrorsPanel errors={latest.errors} />
          )}
        </>
      )}

      <HistorySection history={history} />
    </div>
  );
};

const Summary = ({ latest }) => {
  const running = latest.status === "running";
  const missing = latest.missing?.length || 0;
  const stale = latest.title_stale?.length || 0;
  const scanned = latest.scanned_tmdb || 0;
  return (
    <div className="bg-white border border-[var(--line)] rounded-md p-5 md:p-6">
      <div className="flex items-center gap-3 flex-wrap justify-between">
        <div className="flex items-center gap-3">
          <StatusBadge status={latest.status} />
          <div className="text-xs text-neutral-500 inline-flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" />
            {latest.finished_at
              ? `Dernier scan : ${fmtDate(latest.finished_at)}`
              : `Démarré : ${fmtDate(latest.started_at)}`}
          </div>
        </div>
        {latest.apply && (
          <div className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-[var(--red)] text-[var(--red)]">
            Rattrapage auto activé
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
        <Metric label="Titres TMDB scannés" value={scanned} testId="audit-metric-scanned" />
        <Metric label="Manquants" value={missing} accent={missing > 0} testId="audit-metric-missing" />
        <Metric label="Titres périmés" value={stale} accent={stale > 0} testId="audit-metric-stale" />
        <Metric
          label={latest.apply ? "Corrigés" : "À corriger"}
          value={latest.apply ? (latest.applied_missing || 0) + (latest.applied_titles || 0) : missing + stale}
          testId="audit-metric-fixed"
        />
      </div>
      {running && (
        <div className="mt-4 text-xs text-neutral-500 inline-flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Sweep en cours — 6 plateformes × jusqu'à 100 pages. Le rapport se rafraîchit automatiquement.
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    running: { label: "En cours", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    success: { label: "Terminé", cls: "bg-green-50 text-green-700 border-green-200" },
    partial: { label: "Partiel", cls: "bg-orange-50 text-orange-700 border-orange-200" },
    error: { label: "Échec", cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-neutral-100 text-neutral-700 border-neutral-200" };
  return (
    <span className={`inline-flex text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${s.cls}`}>
      {s.label}
    </span>
  );
};

const Metric = ({ label, value, accent, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</div>
    <div className={`font-display text-3xl mt-1 ${accent ? "text-[var(--red)]" : "text-neutral-900"}`}>
      {value}
    </div>
  </div>
);

const MissingList = ({ rows, onFix, busyRow }) => (
  <div className="bg-white border border-[var(--line)] rounded-md" data-testid="audit-missing-panel">
    <div className="p-4 border-b border-[var(--line)] flex items-center gap-2">
      <AlertTriangle className="h-4 w-4 text-[var(--red)]" />
      <span className="font-display text-lg">Titres manquants</span>
      <span className="text-xs text-neutral-500">({rows.length})</span>
    </div>
    {rows.length === 0 ? (
      <div className="p-6 text-center text-neutral-500 text-sm">
        Aucun titre manquant détecté. Le catalogue est aligné avec TMDB. 🎉
      </div>
    ) : (
      <ul className="divide-y divide-[var(--line)] max-h-[520px] overflow-y-auto">
        {rows.map((r) => {
          const key = `${r.tmdb_id}:missing`;
          const busy = busyRow === key;
          return (
            <li key={r.tmdb_id} className="p-4 flex items-center gap-3" data-testid={`audit-missing-${r.tmdb_id}`}>
              {r.type === "Film" ? <Film className="h-4 w-4 text-neutral-400 shrink-0" /> : <Tv className="h-4 w-4 text-neutral-400 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.title}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  {r.type} · sortie {r.release_date || "—"} · popularité {r.popularity?.toFixed?.(1) ?? r.popularity}
                </div>
              </div>
              <a
                href={`https://www.themoviedb.org/${r.type === "Film" ? "movie" : "tv"}/${r.tmdb_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-neutral-400 hover:text-[var(--red)]"
                title="Voir sur TMDB"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button
                onClick={() => onFix(r, "missing")}
                disabled={busy}
                className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[var(--red)] text-white hover:bg-[var(--red-dark)] disabled:opacity-60"
                data-testid={`audit-add-${r.tmdb_id}`}
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Ajouter
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

const StaleTitlesList = ({ rows, onFix, busyRow }) => (
  <div className="bg-white border border-[var(--line)] rounded-md" data-testid="audit-stale-panel">
    <div className="p-4 border-b border-[var(--line)] flex items-center gap-2">
      <RefreshCw className="h-4 w-4 text-amber-600" />
      <span className="font-display text-lg">Titres périmés</span>
      <span className="text-xs text-neutral-500">({rows.length})</span>
    </div>
    {rows.length === 0 ? (
      <div className="p-6 text-center text-neutral-500 text-sm">
        Aucun titre à rafraîchir. Toutes les traductions FR sont à jour.
      </div>
    ) : (
      <ul className="divide-y divide-[var(--line)] max-h-[520px] overflow-y-auto">
        {rows.map((r) => {
          const key = `${r.tmdb_id}:stale`;
          const busy = busyRow === key;
          return (
            <li key={r.tmdb_id} className="p-4 flex items-center gap-3" data-testid={`audit-stale-${r.tmdb_id}`}>
              {r.type === "Film" ? <Film className="h-4 w-4 text-neutral-400 shrink-0" /> : <Tv className="h-4 w-4 text-neutral-400 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm">
                  <span className="line-through text-neutral-400">{r.old_title}</span>
                  <span className="mx-2 text-neutral-300">→</span>
                  <span className="font-medium">{r.new_title}</span>
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{r.type} · tmdb:{r.tmdb_id}</div>
              </div>
              <button
                onClick={() => onFix(r, "stale")}
                disabled={busy}
                className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[var(--line-strong)] hover:border-black disabled:opacity-60"
                data-testid={`audit-refresh-${r.tmdb_id}`}
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Rafraîchir
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

const ErrorsPanel = ({ errors }) => (
  <details className="bg-white border border-red-200 rounded-md">
    <summary className="p-4 cursor-pointer text-sm text-red-700 font-medium">
      {errors.length} erreur{errors.length > 1 ? "s" : ""} pendant l'audit
    </summary>
    <ul className="p-4 pt-0 text-xs text-red-800 space-y-1 font-mono">
      {errors.slice(0, 30).map((e, i) => (
        <li key={i} className="truncate">{e}</li>
      ))}
      {errors.length > 30 && <li className="italic text-red-500">… et {errors.length - 30} autres</li>}
    </ul>
  </details>
);

const HistorySection = ({ history }) => {
  // Auto-expand the most recent audit on first render so the user sees
  // instantly what was corrected, without having to click. Subsequent
  // manual open/close overrides this.
  const [openId, setOpenId] = useState(history[0]?.started_at || null);

  useEffect(() => {
    // If a fresher audit arrives (poll while a run is in progress), keep
    // the newest one auto-expanded — but only if the user hasn't opened
    // a different row manually.
    if (history[0] && !openId) setOpenId(history[0].started_at);
  }, [history[0]?.started_at]);  // eslint-disable-line

  if (!history || history.length === 0) return null;

  return (
    <section className="mt-4" data-testid="audit-history">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-4 w-4 text-neutral-500" />
        <h2 className="font-display text-xl">Historique des audits</h2>
        <span className="text-xs text-neutral-500">({history.length} dernier{history.length > 1 ? "s" : ""})</span>
      </div>
      <p className="text-xs text-neutral-500 mb-4 max-w-3xl leading-relaxed">
        Chaque audit (manuel ou automatique du dimanche 22 h UTC) est archivé ici avec le détail exact des titres détectés et corrigés. Cliquez sur une ligne pour voir ce que le cron a effectivement modifié.
      </p>
      <div className="bg-white border border-[var(--line)] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-[var(--line)]">
            <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="p-3 w-8"></th>
              <th className="p-3">Date</th>
              <th className="p-3">Origine</th>
              <th className="p-3">Statut</th>
              <th className="p-3 text-right">Scannés</th>
              <th className="p-3 text-right">Manquants</th>
              <th className="p-3 text-right">Périmés</th>
              <th className="p-3 text-right">Corrigés</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => {
              const id = h.started_at;
              const isOpen = openId === id;
              const fixed = (h.applied_missing || 0) + (h.applied_titles || 0);
              const hasDetails = (h.missing_preview?.length || 0) + (h.title_stale_preview?.length || 0) > 0;
              return (
                <React.Fragment key={id}>
                  <tr
                    className={`border-b border-[var(--line)] hover:bg-neutral-50 ${hasDetails ? "cursor-pointer" : ""}`}
                    onClick={() => hasDetails && setOpenId(isOpen ? null : id)}
                    data-testid={`audit-history-row-${id}`}
                  >
                    <td className="p-3">
                      {hasDetails ? (
                        isOpen ? <ChevronDown className="h-4 w-4 text-neutral-400" /> : <ChevronRight className="h-4 w-4 text-neutral-400" />
                      ) : null}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <div>{fmtDate(h.finished_at || h.started_at)}</div>
                      <div className="text-[10px] text-neutral-500">{new Date(h.finished_at || h.started_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    </td>
                    <td className="p-3">
                      <TriggerBadge trigger={h.trigger} apply={h.apply} />
                    </td>
                    <td className="p-3">
                      <StatusBadge status={h.status} />
                    </td>
                    <td className="p-3 text-right tabular-nums text-neutral-600">{h.scanned_tmdb || 0}</td>
                    <td className={`p-3 text-right tabular-nums ${h.missing_count > 0 ? "text-[var(--red)] font-medium" : "text-neutral-400"}`}>{h.missing_count || 0}</td>
                    <td className={`p-3 text-right tabular-nums ${h.title_stale_count > 0 ? "text-amber-700 font-medium" : "text-neutral-400"}`}>{h.title_stale_count || 0}</td>
                    <td className={`p-3 text-right tabular-nums ${fixed > 0 ? "text-green-700 font-medium" : "text-neutral-400"}`}>
                      {h.apply ? fixed : "—"}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-neutral-50/50">
                      <td colSpan={8} className="p-4">
                        <HistoryDetails audit={h} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const TriggerBadge = ({ trigger, apply }) => {
  const isScheduled = trigger === "scheduled";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${
        isScheduled ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-neutral-50 text-neutral-700 border-neutral-200"
      }`}
    >
      {isScheduled ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />}
      {isScheduled ? "Cron auto" : "Manuel"}
      {apply && <span className="ml-0.5 text-[9px] opacity-70">(apply)</span>}
    </span>
  );
};

const HistoryDetails = ({ audit }) => {
  const missing = audit.missing_preview || [];
  const stale = audit.title_stale_preview || [];
  const missingCount = audit.missing_count || 0;
  const staleCount = audit.title_stale_count || 0;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
          Titres manquants détectés ({missingCount})
          {audit.apply && (
            <span className="ml-2 text-green-700 normal-case">
              → {audit.applied_missing || 0} ajouté{(audit.applied_missing || 0) > 1 ? "s" : ""}
            </span>
          )}
        </div>
        {missing.length === 0 ? (
          <div className="text-xs text-neutral-500 italic">Aucun</div>
        ) : (
          <ul className="text-xs space-y-1">
            {missing.map((m) => (
              <li key={m.tmdb_id} className="flex items-center gap-2">
                {m.type === "Film" ? <Film className="h-3 w-3 text-neutral-400 shrink-0" /> : <Tv className="h-3 w-3 text-neutral-400 shrink-0" />}
                <span className="truncate">{m.title}</span>
                <span className="text-neutral-400 shrink-0">· {m.release_date || "—"}</span>
                <span className="text-neutral-400 shrink-0">· pop {m.popularity?.toFixed?.(1) ?? m.popularity}</span>
              </li>
            ))}
            {missingCount > missing.length && (
              <li className="text-neutral-400 italic">… et {missingCount - missing.length} autres</li>
            )}
          </ul>
        )}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
          Titres périmés détectés ({staleCount})
          {audit.apply && (
            <span className="ml-2 text-green-700 normal-case">
              → {audit.applied_titles || 0} rafraîchi{(audit.applied_titles || 0) > 1 ? "s" : ""}
            </span>
          )}
        </div>
        {stale.length === 0 ? (
          <div className="text-xs text-neutral-500 italic">Aucun</div>
        ) : (
          <ul className="text-xs space-y-1">
            {stale.map((s) => (
              <li key={s.tmdb_id} className="flex items-center gap-2">
                {s.type === "Film" ? <Film className="h-3 w-3 text-neutral-400 shrink-0" /> : <Tv className="h-3 w-3 text-neutral-400 shrink-0" />}
                <span className="line-through text-neutral-400 truncate">{s.old_title}</span>
                <span className="text-neutral-300 shrink-0">→</span>
                <span className="truncate">{s.new_title}</span>
              </li>
            ))}
            {staleCount > stale.length && (
              <li className="text-neutral-400 italic">… et {staleCount - stale.length} autres</li>
            )}
          </ul>
        )}
      </div>
      {(audit.errors_count || 0) > 0 && (
        <div className="md:col-span-2 text-xs text-red-700">
          ⚠ {audit.errors_count} erreur{audit.errors_count > 1 ? "s" : ""} pendant cet audit
        </div>
      )}
    </div>
  );
};
